import React, { useEffect, useState } from 'react'
import { Button, Form, FormCheck, FormGroup } from 'react-bootstrap'
import cloneDeep from 'lodash/cloneDeep'
import { Field, FieldArray, Formik, yupToFormErrors } from 'formik'
import { useServiceContext } from '~/root'
import { CollaboratorsValidationSchema } from './WFPhaseValidation'
import { useTranslation } from 'react-i18next'
import { transformFromServer, transformToServer } from '~/pages/settings/workflow-templates/DataService'
import { removeMergedObj } from './ReviewPhaseModal'
import Modal from '../Modal'
import { FsiType } from '~/constants/fsi'
import { WF_STEP_TYPE } from '~/types/workflow'
import CustomErrorMessage from '../ErrorMessage'
import UserGroupSteps from '~/pages/settings/workflow-templates/UserGroupSteps'
import { getFsiEditors, updateFsiEditors } from '~/services/api/files'
import { toast } from '~/services/toast'
import type { FileEditorsProcessed } from '~/services/api/files'

interface EditPhaseModalProps {
  fsi: {
    id: number
    name?: string
    type?: string
  }
  onClose?: () => void
}

function EditPhaseModal({ fsi, onClose }: EditPhaseModalProps) {
  const context = useServiceContext()
  const [isOpen, setIsOpen] = useState(true)
  const [formData, setFormData] = useState<FileEditorsProcessed | {}>({})
  const { t: translate } = useTranslation()
  const collaboratorsValidationSchema = CollaboratorsValidationSchema(translate)

  useEffect(() => {
    if (fsi?.id) {
      getFsiEditors(fsi.id).then((data) => {
        setFormData(transformFromServer(data, undefined))
      })
    }
  }, [fsi])

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  function doSave(values: FileEditorsProcessed | any) {
    const cloneVals = cloneDeep(values)
    const saveRecursivelyVar = cloneVals.saveRecursivelyVar
    delete cloneVals.saveRecursivelyVar
    const finalData = removeMergedObj(transformToServer(cloneVals), 'collaborators')
    updateFsiEditors(fsi.id, finalData as { collaborators: any }, saveRecursivelyVar, false, true).then(
      (resp) => {
        doClose()
      },
      (data: any) => {
        toast.error(data?.data?.message ? data.data.message : translate('SOMETHING_WENT_WRONG_MSG_ERROR'))
      }
    )
  }
  return (
    <Modal show={isOpen} size="lg" title={translate('BROWSER_FILE_SETTINGS_EDITORS_H_TITLE')} handleClose={doClose}>
      <Formik
        innerRef={(el) => {
          el ? (context.store.formRef = el) : null
        }}
        initialValues={{
          ...cloneDeep(formData)
        }}
        enableReinitialize
        validate={(values: FileEditorsProcessed | any) => {
          return collaboratorsValidationSchema
            .validate(values, {
              abortEarly: false,
              context: values
            })
            .then(
              () => {
                return {}
              },
              (err: any) => {
                return yupToFormErrors(err)
              }
            )
        }}
        onSubmit={(values) => {
          doSave(values)
        }}
      >
        {({
          values,
          handleSubmit,
          setFieldValue
        }: {
          values: FileEditorsProcessed | any
          handleSubmit: () => void
          setFieldValue: (field: string, value: any) => void
        }) => (
          <>
            <Modal.Body>
              <div className="form-group md-form-group w100p">
                <label>{fsi?.type === FsiType.FOLDER ? translate('FSI_TYPE_FOLDER') : translate('DOCUMENT')}</label>
                {fsi?.name ? <p className="whitespaceprewrap fs12">{fsi.name}</p> : null}
              </div>
              {values?.workflowTemplate?.id && (
                <div className="form-group md-form-group w100p">
                  <label>{translate('WF_TEMPLATE_LABEL')}</label>
                  <p>{values.workflowTemplate?.name || '-'}</p>
                </div>
              )}
              <FormGroup>
                <Form.Label>{translate('BROWSER_FILE_SETTINGS_EDITORS_TYPE_HEADLINE')}</Form.Label>
                <FormCheck
                  name="collaborators.type"
                  value={WF_STEP_TYPE.SETUP_WITH_WORKFLOW}
                  checked={values?.collaborators?.type === WF_STEP_TYPE.SETUP_WITH_WORKFLOW}
                  type={'radio'}
                  onChange={() => setFieldValue('collaborators.type', WF_STEP_TYPE.SETUP_WITH_WORKFLOW)}
                  id={`e8decccf-1a00-44dc-b9ce-ed6874c8a36c-${WF_STEP_TYPE.SETUP_WITH_WORKFLOW}`}
                  disabled={values?.readOnly}
                  label={
                    <div className="px-2 font-weight-normal">
                      {translate('BROWSER_FILE_SETTINGS_EDITORS_LABEL_SETUP_EDITORS_WITH_CR')}
                    </div>
                  }
                />

                <FormCheck
                  name="collaborators.type"
                  value={WF_STEP_TYPE.USE_PREDEFINED}
                  checked={values?.collaborators?.type === WF_STEP_TYPE.USE_PREDEFINED}
                  type={'radio'}
                  onChange={() => setFieldValue('collaborators.type', WF_STEP_TYPE.USE_PREDEFINED)}
                  id={`e8decccf-1a00-44dc-b9ce-ed6874c8a36c-${WF_STEP_TYPE.USE_PREDEFINED}`}
                  disabled={values?.readOnly}
                  label={
                    <div className="px-2 font-weight-normal">
                      {translate('BROWSER_FILE_SETTINGS_EDITORS_LABEL_USE_PREDEFINED')}
                    </div>
                  }
                />

                <FormCheck
                  name="collaborators.type"
                  value={WF_STEP_TYPE.USE_INITIATOR}
                  checked={values?.collaborators?.type === WF_STEP_TYPE.USE_INITIATOR}
                  type={'radio'}
                  onChange={() => setFieldValue('collaborators.type', WF_STEP_TYPE.USE_INITIATOR)}
                  id={`e8decccf-1a00-44dc-b9ce-ed6874c8a36c-${WF_STEP_TYPE.USE_INITIATOR}`}
                  disabled={values?.readOnly}
                  label={
                    <div className="px-2 font-weight-normal">
                      {translate('BROWSER_FILE_SETTINGS_EDITORS_LABEL_USE_CURRENT')}
                    </div>
                  }
                />
              </FormGroup>

              {values?.collaborators?.type === WF_STEP_TYPE.USE_PREDEFINED ? (
                <FieldArray name="collaborators.steps">
                  {(arrayHelpers) => (
                    <UserGroupSteps
                      arrayHelpers={arrayHelpers}
                      values={values?.collaborators ?? []}
                      nofallTxt={'BROWSER_FILE_SETTINGS_EDITORS_INFO_LABEL'}
                      labelTxt={'BROWSER_FILE_SETTINGS_EDITORS_SEQUENCE_HEADLINE'}
                      disabled={!!values?.readOnly}
                      isFromManageWFModals={true}
                    />
                  )}
                </FieldArray>
              ) : null}

              {/* when no steps are defined in Edit phase, show below error */}
              {!values?.collaborators?.steps || !values?.collaborators?.steps?.length ? (
                <CustomErrorMessage name={`collaborators.steps`} />
              ) : null}
            </Modal.Body>
            <Modal.Footer>
              {!!values?.readOnly && (
                <Button className="btn btn-primary at_btnSaveApprovers" type="button" onClick={doClose}>
                  {translate('OK')}
                </Button>
              )}
              {!values?.readOnly && (
                <>
                  <Button className="btn btn-close" variant="link" type="button" onClick={doClose}>
                    {translate('CANCEL')}
                  </Button>
                  <Button
                    type="submit"
                    className="btn btn-primary at_btnSaveApprovers"
                    onClick={() => {
                      setFieldValue('saveRecursivelyVar', false)
                      handleSubmit()
                    }}
                  >
                    {translate('SAVE_BTN')}
                  </Button>
                </>
              )}
              {fsi?.type === context.constant.file.type.FOLDER && !values?.readOnly && (
                <Button
                  type="submit"
                  className="btn btn-primary at_btnSaveAclsRecursively"
                  onClick={() => {
                    setFieldValue('saveRecursivelyVar', true)
                    handleSubmit()
                  }}
                >
                  {translate('SAVE_FOR_ENCLOSED_ITEMS')}
                </Button>
              )}
            </Modal.Footer>
          </>
        )}
      </Formik>
    </Modal>
  )
}

export default EditPhaseModal
